import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Target, TrendingUp, Flame, Clock, DollarSign,
  Check, X, Zap, Star, Award, ChevronRight, Lock,
} from 'lucide-react';

// ─── Bet On Yourself — Prediction market meets daily challenges ─────
// Inspired by Polymarket, DraftKings, and staking mechanics

interface Challenge {
  id: string;
  title: string;
  description: string;
  difficulty: 'easy' | 'medium' | 'hard' | 'legendary';
  stake: number; // points you put up
  multiplier: number; // payout multiplier if you hit it
  timeframe: string;
  progress?: number; // 0-100, null if not started
  status: 'available' | 'active' | 'won' | 'lost';
  metric: string;
  target: string;
}

const DAILY_CHALLENGES: Challenge[] = [
  {
    id: '1', title: 'Greet the Floor', description: 'Greet every table within 30 seconds of seating',
    difficulty: 'easy', stake: 25, multiplier: 1.5, timeframe: 'Today',
    progress: 80, status: 'active', metric: 'Greeting speed', target: '100% under 30s',
  },
  {
    id: '2', title: 'Three-peat', description: 'Land 3 upsells before noon',
    difficulty: 'medium', stake: 50, multiplier: 2.0, timeframe: 'Before 12 PM',
    progress: 67, status: 'active', metric: 'Upsells', target: '3 before noon',
  },
  {
    id: '3', title: 'Dubai Dreams', description: 'Sell 4+ Dubai Chocolate desserts in one shift',
    difficulty: 'hard', stake: 100, multiplier: 3.0, timeframe: 'Today',
    progress: 25, status: 'active', metric: 'Dubai Chocolate', target: '4 sold',
  },
  {
    id: '4', title: 'The Perfect Shift', description: '90%+ greeting, 75%+ upsell, 5+ drinks — all in one shift',
    difficulty: 'legendary', stake: 200, multiplier: 5.0, timeframe: 'Today',
    progress: 0, status: 'available', metric: 'All metrics', target: 'Perfect scores',
  },
];

const AVAILABLE_BETS: Challenge[] = [
  {
    id: '5', title: 'Weekend Warrior', description: 'Average $220+ per shift across Fri-Sun',
    difficulty: 'medium', stake: 75, multiplier: 2.5, timeframe: 'This weekend',
    status: 'available', metric: 'Avg earnings', target: '$220+/shift',
  },
  {
    id: '6', title: 'Streak Hunter', description: 'Hit a 10+ interaction streak',
    difficulty: 'hard', stake: 100, multiplier: 3.5, timeframe: 'This week',
    status: 'available', metric: 'Best streak', target: '10+ in a row',
  },
  {
    id: '7', title: 'Diamond Hands', description: 'Maintain 70%+ upsell rate for 7 consecutive days',
    difficulty: 'legendary', stake: 250, multiplier: 5.0, timeframe: '7 days',
    status: 'available', metric: 'Upsell rate', target: '70%+ for 7 days',
  },
];

const PAST_BETS: Challenge[] = [
  { id: 'p1', title: 'Morning Rush', description: '5 upsells before 11 AM', difficulty: 'medium', stake: 50, multiplier: 2.0, timeframe: 'Mar 13', status: 'won', metric: 'Upsells', target: '5 by 11 AM' },
  { id: 'p2', title: 'Drink Master', description: '6+ drink promotions', difficulty: 'hard', stake: 100, multiplier: 3.0, timeframe: 'Mar 12', status: 'lost', metric: 'Drink promos', target: '6+ promos' },
  { id: 'p3', title: 'First Impressions', description: '100% greeting rate', difficulty: 'easy', stake: 25, multiplier: 1.5, timeframe: 'Mar 11', status: 'won', metric: 'Greetings', target: '100%' },
  { id: 'p4', title: 'The Closer', description: '80%+ upsell success rate', difficulty: 'hard', stake: 100, multiplier: 3.0, timeframe: 'Mar 10', status: 'won', metric: 'Success rate', target: '80%+' },
  { id: 'p5', title: 'Big Spender Tables', description: 'Avg check size up 15%', difficulty: 'medium', stake: 50, multiplier: 2.0, timeframe: 'Mar 9', status: 'lost', metric: 'Avg check', target: '+15%' },
];

const difficultyConfig = {
  easy: { label: 'Easy', color: 'text-emerald-400', bg: 'bg-emerald-400/10', border: 'border-emerald-500/30' },
  medium: { label: 'Medium', color: 'text-amber-400', bg: 'bg-amber-400/10', border: 'border-amber-500/30' },
  hard: { label: 'Hard', color: 'text-orange-400', bg: 'bg-orange-400/10', border: 'border-orange-500/30' },
  legendary: { label: 'Legendary', color: 'text-purple-400', bg: 'bg-purple-400/10', border: 'border-purple-500/30' },
};

// ─── Component ──────────────────────────────────────────────────────

const BetOnYourself: React.FC = () => {
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState<'active' | 'available' | 'history'>('active');

  const totalStaked = DAILY_CHALLENGES.filter(c => c.status === 'active').reduce((s, c) => s + c.stake, 0);
  const potentialPayout = DAILY_CHALLENGES.filter(c => c.status === 'active').reduce((s, c) => s + c.stake * c.multiplier, 0);
  const wonBets = PAST_BETS.filter(b => b.status === 'won');
  const lostBets = PAST_BETS.filter(b => b.status === 'lost');
  const lifetimePnL = wonBets.reduce((s, b) => s + (b.stake * b.multiplier - b.stake), 0) - lostBets.reduce((s, b) => s + b.stake, 0);

  const renderChallenge = (c: Challenge, showProgress = false) => {
    const dc = difficultyConfig[c.difficulty];
    return (
      <div key={c.id} className={`bg-gray-900 rounded-xl border ${c.status === 'won' ? 'border-emerald-500/30' : c.status === 'lost' ? 'border-red-500/20' : 'border-gray-800'} p-5 transition-all hover:border-gray-700`}>
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className={`text-[10px] font-black uppercase px-1.5 py-0.5 rounded ${dc.bg} ${dc.color}`}>
              {dc.label}
            </span>
            <span className="text-xs text-gray-600">{c.timeframe}</span>
          </div>
          {c.status === 'won' && <Check className="w-5 h-5 text-emerald-400" />}
          {c.status === 'lost' && <X className="w-5 h-5 text-red-400" />}
        </div>

        <h3 className="text-sm font-bold text-white mb-0.5">{c.title}</h3>
        <p className="text-xs text-gray-500 mb-3">{c.description}</p>

        {showProgress && c.progress !== undefined && c.status === 'active' && (
          <div className="mb-3">
            <div className="flex justify-between text-[10px] text-gray-600 mb-1">
              <span>{c.metric}</span>
              <span>{c.progress}%</span>
            </div>
            <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  c.progress >= 100 ? 'bg-emerald-400' : c.progress >= 50 ? 'bg-amber-400' : 'bg-gray-600'
                }`}
                style={{ width: `${c.progress}%` }}
              />
            </div>
          </div>
        )}

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div>
              <div className="text-[10px] text-gray-600">STAKE</div>
              <div className="text-sm font-bold text-white">{c.stake} pts</div>
            </div>
            <div>
              <div className="text-[10px] text-gray-600">MULTIPLIER</div>
              <div className={`text-sm font-bold ${dc.color}`}>{c.multiplier}x</div>
            </div>
            <div>
              <div className="text-[10px] text-gray-600">PAYOUT</div>
              <div className="text-sm font-bold text-emerald-400">{Math.round(c.stake * c.multiplier)} pts</div>
            </div>
          </div>

          {c.status === 'available' && (
            <button className="px-4 py-1.5 text-xs font-bold rounded-lg bg-gradient-to-r from-emerald-500 to-cyan-500 text-white hover:from-emerald-400 hover:to-cyan-400 transition-all active:scale-95">
              STAKE IT
            </button>
          )}
          {c.status === 'won' && (
            <span className="text-xs font-bold text-emerald-400">+{Math.round(c.stake * c.multiplier - c.stake)} pts</span>
          )}
          {c.status === 'lost' && (
            <span className="text-xs font-bold text-red-400">-{c.stake} pts</span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-900/50 to-cyan-900/50 border-b border-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button onClick={() => navigate('/restaurant/mockups')} className="flex items-center gap-1.5 text-gray-500 hover:text-gray-300 text-sm mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Hub
          </button>
          <div className="flex items-center gap-3 mb-2">
            <Target className="w-7 h-7 text-emerald-400" />
            <h1 className="text-2xl font-bold">Bet On Yourself</h1>
          </div>
          <p className="text-gray-500 text-sm">Stake points on your own performance. Win big or learn something.</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Stats bar */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-gray-950 rounded-xl border border-gray-900 p-4">
            <div className="text-[10px] text-gray-600 uppercase mb-1">Active Stakes</div>
            <div className="text-xl font-bold text-white">{totalStaked} pts</div>
            <div className="text-[10px] text-gray-600">across {DAILY_CHALLENGES.filter(c => c.status === 'active').length} bets</div>
          </div>
          <div className="bg-gray-950 rounded-xl border border-gray-900 p-4">
            <div className="text-[10px] text-gray-600 uppercase mb-1">Potential Payout</div>
            <div className="text-xl font-bold text-emerald-400">{Math.round(potentialPayout)} pts</div>
            <div className="text-[10px] text-emerald-600">if you hit everything</div>
          </div>
          <div className="bg-gray-950 rounded-xl border border-gray-900 p-4">
            <div className="text-[10px] text-gray-600 uppercase mb-1">Win Rate</div>
            <div className="text-xl font-bold text-white">{Math.round((wonBets.length / PAST_BETS.length) * 100)}%</div>
            <div className="text-[10px] text-gray-600">{wonBets.length}W / {lostBets.length}L</div>
          </div>
          <div className="bg-gray-950 rounded-xl border border-gray-900 p-4">
            <div className="text-[10px] text-gray-600 uppercase mb-1">Lifetime P&L</div>
            <div className={`text-xl font-bold ${lifetimePnL >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {lifetimePnL >= 0 ? '+' : ''}{lifetimePnL} pts
            </div>
            <div className="text-[10px] text-gray-600">all time</div>
          </div>
        </div>

        {/* Explanation card */}
        <div className="bg-gradient-to-r from-emerald-500/5 to-cyan-500/5 border border-emerald-500/20 rounded-xl p-5">
          <h3 className="text-sm font-bold text-emerald-400 mb-2">How it works</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs text-gray-400">
            <div className="flex items-start gap-2">
              <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-emerald-400 text-[10px] font-bold">1</span>
              </div>
              <div>
                <span className="text-white font-medium">Pick a challenge</span> and stake your points.
                Higher difficulty = higher multiplier.
              </div>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-emerald-400 text-[10px] font-bold">2</span>
              </div>
              <div>
                <span className="text-white font-medium">Work your shift.</span> Your performance is tracked
                automatically. Watch your progress in real time.
              </div>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-emerald-400 text-[10px] font-bold">3</span>
              </div>
              <div>
                <span className="text-white font-medium">Hit the target?</span> You get your stake back
                plus the multiplied payout. Miss? You lose the stake.
                <span className="text-emerald-400"> You can't lose real money — only reward points.</span>
              </div>
            </div>
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex items-center gap-1 bg-gray-950 rounded-lg p-1 border border-gray-900 w-fit">
          {[
            { key: 'active' as const, label: 'Active Bets' },
            { key: 'available' as const, label: 'Available' },
            { key: 'history' as const, label: 'History' },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setSelectedTab(tab.key)}
              className={`px-4 py-1.5 text-xs font-medium rounded-md transition-colors ${
                selectedTab === tab.key ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-400'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="space-y-3">
          {selectedTab === 'active' && DAILY_CHALLENGES.filter(c => c.status === 'active').map(c => renderChallenge(c, true))}
          {selectedTab === 'available' && (
            <>
              <h3 className="text-xs text-gray-600 uppercase tracking-wider">Today's Challenges</h3>
              {DAILY_CHALLENGES.filter(c => c.status === 'available').map(c => renderChallenge(c))}
              <h3 className="text-xs text-gray-600 uppercase tracking-wider mt-4">Weekly Challenges</h3>
              {AVAILABLE_BETS.map(c => renderChallenge(c))}
            </>
          )}
          {selectedTab === 'history' && PAST_BETS.map(c => renderChallenge(c))}
        </div>

        {/* Degen footer */}
        <div className="text-center py-6 space-y-2">
          <p className="text-sm text-gray-600">
            "The only thing I'm betting on is <span className="text-emerald-400 font-bold">myself</span>."
          </p>
          <p className="text-xs text-gray-700">
            Points staked on challenges are returned with multiplied rewards when you win.
            Lost stakes go back into the team reward pool. WAGMI.
          </p>
        </div>
      </div>
    </div>
  );
};

export default BetOnYourself;
